import { useCallback, useEffect, useRef, type RefObject } from "react"
import { useDispatch } from "react-redux"

import { MessageStatus } from "../interfaces"
import type { Message } from "../interfaces"

import { decrementUnreadCount, updateMessage } from "@utils/store/slices"
import { AppDispatch } from "@utils/store/store"
import { useSocket } from "@utils/contexts/SocketContext"

/**
 * Возвращает колбэк для регистрации DOM-элемента сообщения.
 * Сообщение помечается прочитанным когда:
 * - оно появилось во viewport (IntersectionObserver)
 * - вкладка браузера активна
 *
 * Если вкладка неактивна в момент появления — сообщение
 * попадает в очередь и обрабатывается при возврате на вкладку.
 *
 * IntersectionObserver навешивается только после вызова `markChatReady()` —
 * это нужно, чтобы первичное позиционирование скролла (к первому непрочитанному
 * или к концу) успело примениться до того, как наблюдатели отстреляют.
 * Иначе браузер помечает прочитанными сообщения, случайно оказавшиеся
 * в viewport при первом рендере.
 */
export function useMarkAsRead(
    activeChatId: number | null,
    userHash: string | undefined,
    scrollContainerRef?: RefObject<HTMLElement | null>
) {
    const dispatch = useDispatch<AppDispatch>()
    const socket = useSocket()

    // id сообщений, ставших видимыми пока вкладка была неактивна
    const pendingRef = useRef<Map<number, number>>(new Map())
    // Зарегистрированные элементы сообщений, ожидающие навешивания наблюдателя
    const trackedElementsRef = useRef<Map<Element, { messageId: number; chatId: number }>>(
        new Map()
    )
    // Элементы, для которых IntersectionObserver уже создан — защита от дублирования
    const observedElementsRef = useRef<WeakSet<Element>>(new WeakSet())
    // Первичное позиционирование скролла завершено — можно наблюдать
    const isReadyRef = useRef(false)

    const markRead = useCallback(
        (messageId: number, chatId: number): boolean => {
            if (!socket) return false
            const sent = socket.send({ id: messageId, chatId, action: "viewed" })
            if (!sent) return false
            dispatch(updateMessage({ id: messageId, status: MessageStatus.VIEWED }))
            dispatch(decrementUnreadCount(chatId))
            return true
        },
        [socket, dispatch]
    )

    const attachObserver = useCallback(
        (element: Element, messageId: number, chatId: number) => {
            if (observedElementsRef.current.has(element)) return
            observedElementsRef.current.add(element)

            const observer = new IntersectionObserver(
                (entries) => {
                    if (!entries[0].isIntersecting) return

                    if (document.visibilityState === "visible") {
                        const success = markRead(messageId, chatId)
                        if (success) {
                            observer.disconnect()
                        } else {
                            // Сокет не готов — отключаем текущий observer и перезапускаем
                            // через RAF, чтобы поймать элемент снова когда сокет восстановится
                            observer.disconnect()
                            observedElementsRef.current.delete(element)
                            requestAnimationFrame(() => {
                                if (isReadyRef.current && trackedElementsRef.current.has(element)) {
                                    attachObserver(element, messageId, chatId)
                                }
                            })
                        }
                    } else {
                        observer.disconnect()
                        pendingRef.current.set(messageId, chatId)
                    }
                },
                { threshold: 0.5, root: scrollContainerRef?.current ?? null }
            )

            observer.observe(element)
        },
        [markRead, scrollContainerRef]
    )

    // При возврате на вкладку — обрабатываем накопленные сообщения
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== "visible") return

            pendingRef.current.forEach((chatId, messageId) => {
                markRead(messageId, chatId)
            })
            pendingRef.current.clear()
        }

        document.addEventListener("visibilitychange", handleVisibilityChange)
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
    }, [markRead])

    // Сброс при смене чата
    useEffect(() => {
        pendingRef.current.clear()
        trackedElementsRef.current.clear()
        observedElementsRef.current = new WeakSet()
        isReadyRef.current = false
    }, [activeChatId])

    /**
     * Регистрирует DOM-элемент сообщения для наблюдения.
     * Вызывается как ref-колбэк: ref={registerMessageRef(message)}
     */
    const registerMessageRef = useCallback(
        (
            messageId: number | undefined,
            chatId: number,
            externalUserId: string,
            status: MessageStatus
        ) => {
            // Наблюдаем только за чужими непрочитанными
            if (!messageId || externalUserId === userHash || status !== MessageStatus.CREATED)
                return undefined

            return (element: HTMLElement | null) => {
                if (!element) return
                if (trackedElementsRef.current.has(element)) return
                trackedElementsRef.current.set(element, { messageId, chatId })

                if (isReadyRef.current) {
                    attachObserver(element, messageId, chatId)
                }
            }
        },
        [userHash, attachObserver]
    )

    /**
     * Сигнал, что первичное позиционирование скролла завершено.
     * После этого для всех уже зарегистрированных элементов создаются
     * IntersectionObserver'ы — они отстреляют только для тех, что реально
     * видны в финальной позиции.
     */
    const markChatReady = useCallback(() => {
        if (isReadyRef.current) return
        isReadyRef.current = true
        trackedElementsRef.current.forEach(({ messageId, chatId }, element) => {
            attachObserver(element, messageId, chatId)
        })
    }, [attachObserver])

    const markAllRead = useCallback(
        (msgs: Message[]) => {
            msgs.filter(
                (m) => m.id && m.externalUserId !== userHash && m.status === MessageStatus.CREATED
            ).forEach((m) => markRead(m.id!, m.chatId))
        },
        [userHash, markRead]
    )

    return { registerMessageRef, markAllRead, markChatReady }
}
